import React, { useState } from 'react';
import { sortBy, groupBy, reduce } from 'lodash';
import { observable, makeObservable } from 'mobx';
import { Selection } from 'src/domains/sportsbook/webview/components/Selection';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import {
    MoreLessGroup,
    LinkMoreLessWrapper,
    EachWayDetails,
    SelectionsGroup,
    SelectionList,
} from './OutrightMarket.style';
import { ChevronIconWrapper } from 'src/domains/sportsbook/webview/components/marketHeader/MarketHeader.style';
import {
    SelectionsGroupLiItem,
    SelectionsGroupName,
} from 'src/domains/sportsbook/webview/components/selectionsGroup/SelectionGroup.style';
import { observer } from 'src/utils/mobx-react';
import { cut } from './cut';
import { price } from 'src_common/utils/sport/sort';
import { MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { Collapse } from 'src/ui/components/collapse/sports/04/Collapse';
import { isEachWayOffered } from 'src/domains/sportsbook/utils/isEachWayOffered';

const TRUNCATE_ROWS_LIMIT: Record<string, number> = {
    'custom-outrights-market': 4,
};

const HEADER_INFO = (market: MarketModel): JSX.Element | undefined => {
    if (isEachWayOffered(market)) {
        const terms = market.eachWay?.terms[0];
        if (terms !== undefined) {
            return (
                <EachWayDetails>
                    <span>
                        <I18n
                            langKey='events.outright-market.each-way-terms.label'
                            defaultText='Each Way'
                        />
                    </span>

                    <span>{terms.reduction}</span>

                    <span>
                        <I18n
                            langKey='events.outright-market.each-way-terms.places'
                            defaultText='{places} Places'
                            params={{ places: terms.places }}
                        />
                    </span>
                </EachWayDetails>
            );
        }

        return undefined;
    }
};

function getToggleButton(
    opened: boolean,
    left: number,
    onClick: () => void,
    sportsbookVariant: DefaultComponentVariantType | null
): JSX.Element {
    return (
        <MoreLessGroup variant={sportsbookVariant}>
            <LinkMoreLessWrapper
                onClick={onClick}
                variant={sportsbookVariant}
            >
                {opened ? (
                    <>
                        <I18n
                            langKey='events.request-a-bet-selection.more'
                            defaultText='More'
                        />{' '}
                        ({left})
                    </>
                ) : (
                    <I18n
                        langKey='events.request-a-bet-selection.less'
                        defaultText='Less'
                    />
                )}

                <ChevronIconWrapper position={opened ? 'down' : 'up'} />
            </LinkMoreLessWrapper>
        </MoreLessGroup>
    );
}

function getHeaderInfo(market: MarketModel): JSX.Element | undefined {
    return HEADER_INFO(market) ?? undefined;
}

function groupSelections(selections: Record<string, SelectionModel[]>): Record<string, SelectionModel[]> {
    const selectionListNewFirst = Object.values(selections)[0];
    if (selectionListNewFirst === undefined) {
        return {};
    }
    const selectionListNew: SelectionModel[] = sortBy(selectionListNewFirst, price);

    //bug is in here but don't know if we want to fix it
    return groupBy(selectionListNew, (_selection) => {
        return undefined;
        // return selectionListNew.indexOf(selection) % columns;
    });
}

interface PropsType {
    markets: Array<MarketId>;
    selections: Record<number, SelectionModel[]>;
    isOpen: boolean;
    onClick(): void;
}

class OutrightMarketState {
    @observable public truncate: boolean = true;

    public constructor() {
        makeObservable(this);
    }

    public toggleTruncate = (): void => {
        this.truncate = !this.truncate;
    };
}

const getColumns = (selections: Record<number, SelectionModel[]>): Record<string, Array<SelectionModel>> => {
    return groupSelections(selections);
};

const market = (markets: Array<MarketId>): MarketModel | null => {
    const marketId = markets[0];
    if (marketId !== undefined) {
        return marketId.getModel();
    }
    return null;
};

export const OutrightMarket = observer('OutrightMarket', (props: PropsType) => {
    const [state] = useState(() => new OutrightMarketState());
    const { truncate, toggleTruncate } = state;

    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const languagesState = LanguagesState.get(common);

    const { sportsbookVariant } = configState.componentVariants;

    const marketModel = market(props.markets);
    if (marketModel === null) {
        return null;
    }

    const columns = getColumns(props.selections);
    const { isOpen, onClick } = props;
    const templateId = marketModel.newTemplateId;
    const numOfSelections = reduce(Object.values(columns), (sum, column) => sum + column.length, 0);
    const rowsLimit = TRUNCATE_ROWS_LIMIT[templateId];

    const left =
        rowsLimit !== undefined && rowsLimit * Object.values(columns).length < numOfSelections
            ? numOfSelections - Object.values(columns).length * rowsLimit
            : undefined;
    const isOpened = rowsLimit === undefined ? true : truncate;
    const truncateButton =
        left === undefined ? undefined : getToggleButton(isOpened, left, toggleTruncate, sportsbookVariant);

    return (
        <div className='market-group--outright'>
            <Collapse
                handleToggle={onClick}
                isOpen={isOpen}
                title={languagesState.getMarketTranslationName(
                    marketModel.name,
                    marketModel.eventModel?.participants ?? null
                )}
            >
                {getHeaderInfo(marketModel)}
            </Collapse>

            {isOpen ? (
                <>
                    <SelectionsGroup
                        className='selections-group'
                        variant={sportsbookVariant}
                    >
                        {Object.keys(columns).map((column) => (
                            <SelectionList key={column}>
                                {cut(columns[column] ?? [], truncate ? rowsLimit ?? 0 : 0).map((selection) => (
                                    <SelectionsGroupLiItem
                                        key={selection.id}
                                        variant={sportsbookVariant}
                                    >
                                        <SelectionsGroupName
                                            variant={sportsbookVariant}
                                            title={languagesState.getSelectionTranslationName(selection)}
                                        >
                                            {languagesState.getSelectionTranslationName(selection)}
                                        </SelectionsGroupName>

                                        <Selection
                                            borderBottom={true}
                                            borderLeft={true}
                                            borderRight={true}
                                            selectionId={selection.id2}
                                        />
                                    </SelectionsGroupLiItem>
                                ))}
                            </SelectionList>
                        ))}
                    </SelectionsGroup>
                    {truncateButton}
                </>
            ) : null}
        </div>
    );
});
